/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import shuffleArray from '../utils/shuffleArray'
import { heatedBaseGameDeck, Card } from '../utils/cards'
import { io, Socket } from 'socket.io-client'
import useSound from 'use-sound'
import {
    DndContext,
    pointerWithin,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import 'bootstrap/dist/css/bootstrap.min.css';
import { Volume2, VolumeX, Music4 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import "./css/heatedGame.css";

// todo the import errors
// @ts-expect-error
import bgMusic from '../assets/sounds/music/short-action-rock-loop-1.mp3';
import cardBackImg from '../assets/card-back.png'
import ModernCardActionModal from './ModernCardActionModal';
import CurrentPlayerHand from './CurrentPlayerHand'
import OtherPlayerHand from './OtherPlayerHand'
import { TopInformationBar } from './TopInformationBar'
import { DropZone } from './DropZone'
import Lobby from './Lobby'
import heatedLogo from '../assets/logo.png'

export type User = { id: string, name: string, room: string, hand: Card[] }

type GameState = {
    winner: string;
    turn: string;
    users: User[];
    currentColor: string;
    playedCardsPile: Card[];
    drawCardPile: Card[];
    orderOfPlay: string;
    slapState: boolean;
    slapTracker: { id: string }[];
    fairPlay: { id: string, amount: number, color: string };
    apocalypse: {
        active: boolean,
        color: string,
        tracker: { id: string, name: string, canDiscard: boolean, actionTaken: boolean }[]
    };
    sayHeatedTracker: {
        active: boolean,
        tracker: { id: string, name: string, saidHeated: boolean }[]
    };
    cardTargetUsers: string[];
    timerLength: number;
    responseWindow: {
        active: boolean;
        triggerCard: Card | null;
        triggerPlayer: string;
        targetedPlayers: string[];
        timeRemaining: number;
        responses: { playerId: string, responded: boolean }[];
        stackCount: number;
        stackCards: Card[];
        stackPlayers: string[];
        playerMakingSelection: string | null;
    };
}

let socket: Socket;
let ENDPOINT = "wss://play-heated-online-websockets-05947a3f8066.herokuapp.com/"

// if (window.location.hostname === "localhost") {
//     ENDPOINT = 'http://localhost:5000'
// }

const HeatedGame = () => {
    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)
    const data = { roomCode: searchParams.get('roomCode') }

    // Socket state
    const [room] = useState(data.roomCode)
    const [roomFull, setRoomFull] = useState(false)
    const [users, setUsers] = useState<User[]>([])
    const [currentUser, setCurrentUser] = useState<User>({} as User)
    const [modalType, setModalType] = useState<"ghostPepper" | "fairPlay" | "gettingHeated" | "apocalypse" | "colorPicker" | "swap" | "stackingTarget" | null>(null);

    // Lobby state
    const [gameStarted, setGameStarted] = useState(false)
    const [roomCreator, setRoomCreator] = useState<string | null>(null)

    // Core game state
    const [gameOver, setGameOver] = useState(false)
    const [winner, setWinner] = useState('')
    const [turn, setTurn] = useState('')
    const [currentColor, setCurrentColor] = useState('')
    const [povPlayerIndex, setPovPlayerIndex] = useState(-1);
    const [povPlayerHand, setPovPlayerHand] = useState<Card[]>([])
    const [playedCardsPile, setPlayedCardsPile] = useState<Card[]>([])
    const [drawCardPile, setDrawCardPile] = useState<Card[]>([])
    const [orderOfPlay, setOrderOfPlay] = useState('');

    // Special card states
    const [slapState, setSlapState] = useState(false);
    const [slapTracker, setSlapTracker] = useState<{ id: string }[]>([]);
    const [fairPlay, setFairPlay] = useState<{ id: string, amount: number, color: string }>({ id: '', amount: 0, color: '' });
    const [apocalypse, setApocalypse] = useState<{ active: boolean, color: string, tracker: { id: string, name: string, canDiscard: boolean, actionTaken: boolean }[] }>({ active: false, color: '', tracker: [] });
    const [sayHeatedTracker, setSayHeatedTracker] = useState<{ active: boolean, tracker: { id: string, name: string, saidHeated: boolean }[] }>({ active: false, tracker: [] });

    // Card effect states
    const [cardTargetUsers, setCardTargetUsers] = useState<string[]>([]);

    // Response window state
    const [responseWindow, setResponseWindow] = useState<{
        active: boolean;
        triggerCard: Card | null;
        triggerPlayer: string;
        targetedPlayers: string[];
        timeRemaining: number;
        responses: { playerId: string, responded: boolean }[];
        stackCount: number;
        stackCards: Card[];
        stackPlayers: string[];
        playerMakingSelection: string | null;
    }>({
        active: false,
        triggerCard: null,
        triggerPlayer: '',
        targetedPlayers: [],
        timeRemaining: 0,
        responses: [],
        stackCount: 0,
        stackCards: [],
        stackPlayers: [],
        playerMakingSelection: null
    });

    // UI state
    const [dragAndDropOverlayImg, setDragAndDropOverlayImg] = useState("");
    const [offeredCards, setOfferedCards] = useState<{ playerId: string; cardId: string; }[]>([]);
    const [hasOfferedCardBeenAcceptedOrRejectedOnThisTurn, setHasOfferedCardBeenAcceptedOrRejectedOnThisTurn] = useState(false);
    const [cardEffectIsBeingResolved, setCardEffectIsBeingResolved] = useState(false);

    // Audio state
    const [isSoundMuted, setSoundMuted] = useState(false)
    const [isMusicMuted, setMusicMuted] = useState(true);
    const [playBgMusic, { stop: stopBgMusic }] = useSound(bgMusic, { soundEnabled: !isMusicMuted, interrupt: true, loop: true })

    // Refs and sensors
    const topOfDiscardPileRef = useRef<any>();
    const drawnCardRef = useRef<any>();
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 15 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );



    // =============================================================================
    // UTILITY FUNCTIONS - Pure functions for game logic
    // =============================================================================

    // const canStackCard = (card: Card, lastPlayedCard: Card, isPlayerTargeted: boolean): boolean => {
    //     // Exact same cards can always stack
    //     if (card.name === lastPlayedCard.name && card.color === lastPlayedCard.color) {
    //         return true;
    //     }

    //     // Only targeted players can stack certain cards
    //     if (!isPlayerTargeted) return false;

    //     if (card.name === "Draw 2" && lastPlayedCard.name === "Draw 2") return true;
    //     if (card.name === "Draw Four" && lastPlayedCard.name === "Draw Four") return true;
    //     if (card.name === "Getting Heated" && (lastPlayedCard.name === "Draw 2" || lastPlayedCard.name === "Getting Heated")) return true;

    //     return false;
    // };

    const getNextPlayerId = (isReversing = false): string => {
        let nextPlayerIndex = 0;
        let direction = orderOfPlay;
        if (isReversing) {
            direction = orderOfPlay === "standard" ? "reverse" : "standard";
        }

        for (let u = 0; u < users.length; u++) {
            if (users[u].id === turn) {
                if (direction === "reverse") {
                    nextPlayerIndex = u === 0 ? users.length - 1 : u - 1;
                } else {
                    nextPlayerIndex = u === users.length - 1 ? 0 : u + 1;
                }
            }
        }
        return users[nextPlayerIndex].id;
    };

    const getNextPlayerIdAfterPlayer = (playerId: string): string => {
        let nextPlayerIndex = 0;
        for (let u = 0; u < users.length; u++) {
            if (users[u].id === playerId) {
                if (orderOfPlay === "reverse") {
                    nextPlayerIndex = u === 0 ? users.length - 1 : u - 1;
                } else {
                    nextPlayerIndex = u === users.length - 1 ? 0 : u + 1;
                }
                break;
            }
        }
        return users[nextPlayerIndex].id;
    };

    const skipNextPlayerId = (): string => {
        let nextPlayerIndex = 0;
        for (let u = 0; u < users.length; u++) {
            if (orderOfPlay === "reverse") {
                if (users[u].id === currentUser.id) {
                    if (u === 0) {
                        nextPlayerIndex = users.length - 2;
                    } else if (u === 1) {
                        nextPlayerIndex = users.length - 1;
                    } else {
                        nextPlayerIndex = u - 2;
                    }
                }
            } else {
                if (users[u].id === currentUser.id) {
                    if (u === users.length - 1) {
                        nextPlayerIndex = 1;
                    } else if (u === users.length - 2) {
                        nextPlayerIndex = 0
                    } else {
                        nextPlayerIndex = u + 2;
                    }
                }
            }
        }
        return users[nextPlayerIndex].id;
    };

    const isCardPlayable = (playedCard: Card): boolean => {
        // Can't play if it's not your turn unless special conditions
        if (turn !== currentUser.id && !apocalypse.active && !fairPlay.id) {
            return false;
        }

        const topCard = playedCardsPile[playedCardsPile.length - 1];
        if (!topCard || !playedCard) return false;

        // Black and white cards can always be played
        if (playedCard.color === "Black" || playedCard.color === "White") return true;

        // Same color
        if (currentColor === playedCard.color) return true;

        // Same number
        if (topCard.type === "Number" && playedCard.type === "Number" && topCard.number === playedCard.number) return true;

        // Same action
        if (topCard.type === "Action" && playedCard.type === "Action" && topCard.description === playedCard.description) return true;

        return false;
    };

    // =============================================================================
    // RESPONSE WINDOW HELPERS
    // =============================================================================

    const checkForNoThanksCards = (targetedPlayers: string[]): string[] => {
        return targetedPlayers.filter(playerId => {
            const player = users.find(u => u.id === playerId);
            return player && player.hand.some(card => card.name === "No, Thanks");
        });
    };

    const canStackCard = (card: Card, lastPlayedCard: Card): boolean => {
        // Draw 2 can stack with Draw 2 (any color) - only targeted players
        if (card.name === "Draw 2" && lastPlayedCard.name === "Draw 2") return true;

        // Draw 4 can stack with Draw 4 - any player can stack (out-of-order)
        if (card.name === "Draw Four" && lastPlayedCard.name === "Draw Four") return true;

        // Getting Heated can stack with Getting Heated - any player can stack (out-of-order)
        if (card.name === "Getting Heated" && lastPlayedCard.name === "Getting Heated") return true;

        return false;
    };

    const checkForStackableCards = (targetedPlayers: string[], lastPlayedCard: Card): string[] => {
        return targetedPlayers.filter(playerId => {
            const player = users.find(u => u.id === playerId);
            return player && player.hand.some(card => canStackCard(card, lastPlayedCard));
        });
    };

    const startResponseWindow = (triggerCard: Card, targetedPlayers: string[], forceColor?: string) => {
        if (socket) {
            socket.emit('startResponseWindow', {
                triggerCard,
                triggerPlayer: currentUser.id,
                targetedPlayers,
                currentGameState: {
                    users: users,
                    playedCardsPile: playedCardsPile,
                    drawCardPile: drawCardPile,
                    currentColor: forceColor || currentColor,
                    turn: turn,
                    orderOfPlay: orderOfPlay
                }
            });
        }
    };

    // =============================================================================
    // CARD EFFECT HANDLERS - One place for each effect
    // =============================================================================

    const handleDraw2 = (playedCard: Card) => {
        const nextPlayer = getNextPlayerId();
        const targetedPlayers = [nextPlayer];
        setCardTargetUsers(targetedPlayers);

        // Always update game state first to show the card in discard pile
        updateGameState({
            users: users,
            playedCardsPile: playedCardsPile,
            currentColor: playedCard.color,
            cardTargetUsers: targetedPlayers
        });

        // Check if any targeted players have "No, Thanks" cards or stackable cards
        const playersWithNoThanks = checkForNoThanksCards(targetedPlayers);
        const playersWithStackable = checkForStackableCards(targetedPlayers, playedCard);

        if (playersWithNoThanks.length > 0 || playersWithStackable.length > 0) {
            // Start response window
            startResponseWindow(playedCard, targetedPlayers);
        } else {
            // Execute effect immediately and advance turn
            makePlayerDrawCards(nextPlayer, 2);
            updateGameState({
                users: users,
                playedCardsPile: playedCardsPile,
                currentColor: playedCard.color,
                cardTargetUsers: targetedPlayers,
                turn: getNextPlayerId()
            });
        }
    };

    const handleDraw4 = (selectedColor: string) => {
        const nextPlayer = getNextPlayerId();
        const targetedPlayers = [nextPlayer];
        setCardTargetUsers(targetedPlayers);
        setCurrentColor(selectedColor);

        // Create a Draw 4 card object for checking stackability
        const draw4Card: Card = {
            id: 'Draw-Four-Effect',
            color: 'Black',
            type: 'Action',
            name: 'Draw Four',
            image: '',
            description: 'The next player must draw 4 cards.'
        };

        // Check if any targeted players have "No, Thanks" cards or stackable cards
        const playersWithNoThanks = checkForNoThanksCards(targetedPlayers);
        const playersWithStackable = checkForStackableCards(targetedPlayers, draw4Card);

        if (playersWithNoThanks.length > 0 || playersWithStackable.length > 0) {
            startResponseWindow(draw4Card, targetedPlayers, selectedColor);
        } else {
            // Execute effect immediately
            makePlayerDrawCards(nextPlayer, 4);
            setTurn(getNextPlayerId());
            updateGameState({
                turn: getNextPlayerId(),
                currentColor: selectedColor,
                cardTargetUsers: [nextPlayer],
                users: users
            });
        }
    };

    const handleGettingHeated = (selectedPlayerId: string, selectedColor: string) => {
        const targetedPlayers = [selectedPlayerId];
        setCardTargetUsers(targetedPlayers);
        setCurrentColor(selectedColor);

        // Always update game state first to show the card in discard pile
        updateGameState({
            users: users,
            playedCardsPile: playedCardsPile,
            currentColor: selectedColor,
            cardTargetUsers: targetedPlayers
        });

        // Create a Getting Heated card object for checking stackability
        const gettingHeatedCard: Card = {
            id: 'Getting-Heated-Effect',
            color: 'Black',
            type: 'Action',
            name: 'Getting Heated',
            image: '',
            description: 'Force any player to draw 2 cards.'
        };

        // Check if any targeted players have "No, Thanks" cards or stackable cards
        const playersWithNoThanks = checkForNoThanksCards(targetedPlayers);
        const playersWithStackable = checkForStackableCards(targetedPlayers, gettingHeatedCard);

        if (playersWithNoThanks.length > 0 || playersWithStackable.length > 0) {
            startResponseWindow(gettingHeatedCard, targetedPlayers, selectedColor);
        } else {
            // Execute effect immediately and advance turn
            makePlayerDrawCards(selectedPlayerId, 2);
            updateGameState({
                turn: getNextPlayerId(),
                currentColor: selectedColor,
                cardTargetUsers: targetedPlayers,
                users: users
            });
        }
    };

    // =============================================================================
    // GAME STATE MANAGEMENT
    // =============================================================================

    const updateGameState = useCallback((updatedValues: Partial<GameState>) => {
        const currentGameState = {
            winner, turn, playedCardsPile, users, currentColor, drawCardPile, orderOfPlay,
            slapState, slapTracker, fairPlay, apocalypse, sayHeatedTracker, cardTargetUsers
        };

        const newState = { ...currentGameState, ...updatedValues };

        // Check for winner
        if (newState.users) {
            const winningUser = newState.users.find(user => user.hand.length === 0);
            if (winningUser) {
                newState.winner = winningUser.name;
                setGameOver(true);
                setWinner(winningUser.name);
            }
        }

        socket.emit('updateGameState', newState);
    }, [winner, turn, playedCardsPile, users, currentColor, drawCardPile, orderOfPlay, slapState, slapTracker, fairPlay, apocalypse, sayHeatedTracker, cardTargetUsers]);

    const makePlayerDrawCards = (playerId: string, numberOfCards: number) => {
        const userIndex = users.findIndex(user => user.id === playerId);
        if (userIndex !== -1 && drawCardPile.length >= numberOfCards) {
            const cardsToDraw = drawCardPile.splice(0, numberOfCards);
            users[userIndex].hand = [...users[userIndex].hand, ...cardsToDraw];

            updateGameState({
                users: users,
                drawCardPile: drawCardPile
            });
        }
    };

    // =============================================================================
    // CARD CLICK HANDLER - Simple routing
    // =============================================================================

    const handleCardClick = (card: Card, index: number, playedCardImageRef: any) => {
        // Block all card interactions if someone else is making a selection
        if (responseWindow.playerMakingSelection && responseWindow.playerMakingSelection !== currentUser.id) {
            return;
        }

        // Response window - handle "No, Thanks" or stacking
        if (responseWindow.active && !gameOver) {
            const isTargeted = responseWindow.targetedPlayers.includes(currentUser.id);
            // Handle "No, Thanks" card (only targeted players can use)
            if (isTargeted && card.name === "No, Thanks") {
                    // Play the "No, Thanks" card to the discard pile
                    playedCardsPile.push(card);
                    users[povPlayerIndex].hand.splice(index, 1);

                    // Update game state to show the card was played
                    updateGameState({
                        playedCardsPile: playedCardsPile,
                        users: users
                    });

                    // Send response to server - this stops all stacking
                    if (socket) {
                        socket.emit('playNoThanksResponse', card.id);
                    }
                    return;
                }

            // Handle stackable cards (any player can stack Getting Heated or Draw 4)
            if (responseWindow.triggerCard && canStackCard(card, responseWindow.triggerCard)) {
                const canOutOfOrderStack = card.name === "Getting Heated" || card.name === "Draw Four" || card.name === "Draw 4";

                // Allow stacking if: player is targeted OR it's an out-of-order stackable card
                if (isTargeted || canOutOfOrderStack) {
                    // Play the stacking card to the discard pile
                    playedCardsPile.push(card);
                    users[povPlayerIndex].hand.splice(index, 1);

                    // Update game state to show the card was played
                    updateGameState({
                        playedCardsPile: playedCardsPile,
                        users: users
                    });

                    // Check if this requires target selection
                    if (card.name === "Getting Heated" || card.name === "Draw Four" || card.name === "Draw 4") {
                        // Pause response window and show target selection modal
                        setResponseWindow(prev => ({ ...prev, active: false }));
                        setModalType(card.name === "Getting Heated" ? "stackingTarget" : "colorPicker");

                        // Send pause event to server
                        if (socket) {
                            if (card.name === "Getting Heated") {
                                socket.emit('pauseResponseWindowForTarget', {
                                    cardId: card.id,
                                    card: card
                                });
                            } else {
                                // For Draw 4, we need a different handler for out-of-order stacking
                                socket.emit('pauseResponseWindowForColorSelection', {
                                    cardId: card.id,
                                    card: card
                                });
                            }
                        }
                    } else {
                        // For Draw 2 and Draw 4, proceed normally (no target selection needed)
                        if (socket) {
                            socket.emit('playStackResponse', {
                                cardId: card.id,
                                card: card
                            });
                        }
                    }
                    return;
                }
            }
        }

        // Same exact card can be played
        const lastPlayedCard = playedCardsPile[playedCardsPile.length - 1];

        // Fair Play discarding
        if (fairPlay.id === currentUser.id) {
            playedCardsPile.unshift(card);
            users[povPlayerIndex].hand.splice(index, 1);
            const newAmount = fairPlay.amount - 1;
            if (newAmount === 0) {
                setFairPlay({ id: '', amount: 0, color: '' });
            } else {
                setFairPlay(prev => ({ ...prev, amount: newAmount }));
            }
            return;
        }

        // Apocalypse discarding
        if (apocalypse.active && apocalypse.tracker.some(user => user.id === currentUser.id)) {
            if (card.color === apocalypse.color || card.name === "No, Thanks") {
                playedCardsPile.push(card);
                users[povPlayerIndex].hand.splice(index, 1);

                const updatedTracker = apocalypse.tracker.map(user =>
                    user.id === currentUser.id ? { ...user, actionTaken: true } : user
                );

                const allDone = updatedTracker.every(user => user.actionTaken);
                if (allDone) {
                    setApocalypse({ active: false, color: '', tracker: [] });
                    setTurn(getNextPlayerId());
                } else {
                    setApocalypse(prev => ({ ...prev, tracker: updatedTracker }));
                }
            }
            return;
        }

        if (card.name === lastPlayedCard.name && card.color === lastPlayedCard.color) {
            playCard(card, index, users[povPlayerIndex].id);
            return;
        }

        // Regular turn play
        if (turn === currentUser.id && isCardPlayable(card)) {
            playCard(card, index);
        }
    };

    // =============================================================================
    // MAIN CARD PLAY FUNCTION
    // =============================================================================

    const playCard = async (playedCard: Card, cardIndexInPlayerHand: number, outOfTurnPlayerId?: string) => {
        // Block all card plays if someone else is making a selection
        if (responseWindow.playerMakingSelection && responseWindow.playerMakingSelection !== currentUser.id) {
            return;
        }

        // Play the card
        playedCardsPile.push(playedCard);
        users[povPlayerIndex].hand.splice(cardIndexInPlayerHand, 1);

        // Handle card effects
        if (playedCard.color === "Black") {
            if (playedCard.name === "Getting Heated") {
                setModalType("gettingHeated");
            } else if (playedCard.name === "Ghost Pepper Spray") {
                setModalType("ghostPepper");
            } else if (playedCard.name === "Fair Play") {
                setModalType("fairPlay");
            } else if (playedCard.name === "Apocalypse") {
                setModalType("apocalypse");
            } else if (playedCard.name === "Draw Four") {
                setModalType("colorPicker");
            } else {
                setModalType("colorPicker");
            }
        } else if (playedCard.name === "Swap") {
            setModalType("swap");
        } else if (playedCard.name === "Too Hot") {
            setSlapState(true);
            updateGameState({ turn: currentUser.id, currentColor: playedCard.color, slapState: true });
        } else if (playedCard.name === "Draw 2") {
            handleDraw2(playedCard);
        } else if (playedCard.name === "Reverse") {
            const newOrder = orderOfPlay === "standard" ? "reverse" : "standard";
            setOrderOfPlay(newOrder);
            updateGameState({
                users: users,
                playedCardsPile: playedCardsPile,
                turn: getNextPlayerId(true),
                currentColor: playedCard.color,
                orderOfPlay: newOrder
            });
        } else if (playedCard.name === "Cool Off") {
            updateGameState({
                users: users,
                playedCardsPile: playedCardsPile,
                turn: skipNextPlayerId(),
                currentColor: playedCard.color
            });
        } else {
            // Regular card
            const nextPlayerId = outOfTurnPlayerId ? getNextPlayerIdAfterPlayer(outOfTurnPlayerId) : getNextPlayerId();
            updateGameState({
                users: users,
                playedCardsPile: playedCardsPile,
                turn: nextPlayerId,
                currentColor: playedCard.color !== 'White' ? playedCard.color : currentColor
            });
        }
    };

    // =============================================================================
    // MODAL HANDLERS
    // =============================================================================

    const onHideColorPicker = (selectedColor: string) => {
        setModalType(null);
        const playedCard = playedCardsPile[playedCardsPile.length - 1];

        // Check if we're in a response window context (stacking scenario)
        if (responseWindow.active === false && responseWindow.stackCards.length > 0) {
            // This is a Draw 4 being stacked during a response window
            if (socket) {
                socket.emit('resumeResponseWindowWithColorSelection', {
                    selectedColor: selectedColor,
                    card: playedCard
                });
            }
        } else if (playedCard.name === "Draw Four") {
            // Regular Draw 4 play (not stacking)
            handleDraw4(selectedColor);
        } else {
            // Other color picker uses
            setCurrentColor(selectedColor);
            updateGameState({
                currentColor: selectedColor,
                turn: getNextPlayerId(),
                cardTargetUsers: cardTargetUsers
            });
        }
    };

    const handleCloseGettingHeatedModal = (selectedId: string, selectedColor: string) => {
        setModalType(null);
        handleGettingHeated(selectedId, selectedColor);
        // updateGameState({
        //     currentColor: selectedColor,
        //     cardTargetUsers: cardTargetUsers,
        // });
    };

    const handleCloseGhostPepperModal = (drawSelections: { [playerId: string]: number }, selectedColor: string) => {
        setModalType(null);
        const targetIds = Object.keys(drawSelections);
        setCurrentColor(selectedColor);

        // Check if any targeted players have "No, Thanks" cards
        const playersWithNoThanks = checkForNoThanksCards(targetIds);

        if (playersWithNoThanks.length > 0) {
            // Create a fake card object for Ghost Pepper Spray with draw selections
            const ghostPepperCard: Card = {
                id: 'Ghost-Pepper-Effect',
                color: 'Black',
                type: 'Action',
                name: 'Ghost Pepper Spray',
                image: '',
                description: 'Number all other players in any order, starting from 1. They must draw cards equal to the number they are assigned.'
            };
            // Store draw selections for later use
            setCardTargetUsers(targetIds);
            // Start response window with a reference to draw amounts
            startResponseWindow(ghostPepperCard, targetIds);
        } else {
            // Execute effect immediately
            targetIds.forEach(playerId => {
                makePlayerDrawCards(playerId, drawSelections[playerId]);
            });
            updateGameState({
                turn: getNextPlayerId(),
                currentColor: selectedColor,
                users: users
            });
        }
    };

    const handleCloseSwapModal = (selectedId: string) => {
        setModalType(null);
        // Swap hands
        const targetUserIndex = users.findIndex(user => user.id === selectedId);
        if (targetUserIndex !== -1) {
            const tempHand = users[povPlayerIndex].hand;
            users[povPlayerIndex].hand = users[targetUserIndex].hand;
            users[targetUserIndex].hand = tempHand;
        }
        updateGameState({
            turn: getNextPlayerId(),
            users: users
        });
    };

    const handleCloseFairPlayModal = (selectedId: string, selectedColor: string, discardAmount: number) => {
        setModalType(null);
        setCurrentColor(selectedColor);

        if (discardAmount === 0) {
            setTurn(getNextPlayerId());
        } else {
            setFairPlay({ id: selectedId, amount: discardAmount, color: selectedColor });
            setTurn(selectedId);
        }

        updateGameState({
            turn: discardAmount === 0 ? getNextPlayerId() : selectedId,
            currentColor: selectedColor,
            fairPlay: { id: selectedId, amount: discardAmount, color: selectedColor }
        });
    };

    const handleCloseApocalypseModal = (selectedColor: string) => {
        setModalType(null);
        setCurrentColor(selectedColor);

        const otherUsers = users.filter(user => user.id !== currentUser.id).map(user => ({
            id: user.id,
            name: user.name,
            canDiscard: user.hand.some(card => card.color === selectedColor || card.name === "No, Thanks"),
            actionTaken: false
        }));
        setApocalypse({ active: true, color: selectedColor, tracker: otherUsers });
        setTurn(otherUsers[0].id);
        updateGameState({
            turn: otherUsers[0].id,
            currentColor: selectedColor,
            apocalypse: { active: true, color: selectedColor, tracker: otherUsers }
        });
    };

    const handleCloseStackingTargetModal = (selectedPlayerId: string, selectedColor: string) => {
        setModalType(null);

        // Resume response window with new target and reset timer
        if (socket) {
            socket.emit('resumeResponseWindowWithTarget', {
                targetPlayerId: selectedPlayerId,
                newColor: selectedColor
            });
        }
    };

    const modalCloseHandlers = {
        colorPicker: onHideColorPicker,
        gettingHeated: handleCloseGettingHeatedModal,
        swap: handleCloseSwapModal,
        ghostPepper: handleCloseGhostPepperModal,
        fairPlay: handleCloseFairPlayModal,
        apocalypse: handleCloseApocalypseModal,
        stackingTarget: handleCloseStackingTargetModal
    };

    // =============================================================================
    // UI HELPER FUNCTIONS
    // =============================================================================

    const renderPlayerText = () => {
        let result = '';
        // const lastPlayedCard = playedCardsPile[playedCardsPile.length - 1];

        // Check if someone else is making a selection
        if (responseWindow.playerMakingSelection && responseWindow.playerMakingSelection !== currentUser.id) {
            const playerName = users.find(u => u.id === responseWindow.playerMakingSelection)?.name;
            result += `Waiting for ${playerName} to make their selection...`;
            return result; // Return early to avoid other messages
        }

        if (slapState) {
            const hasSlapped = slapTracker.some(({ id }) => id === currentUser.id);
            result += hasSlapped ? `Too Hot! Waiting for Other Players to Slap` : `Too Hot! Click the Slap Button`;
        }

        if (apocalypse.active) {
            const apocalypseUser = apocalypse.tracker.find(user => user.id === currentUser.id);
            if (apocalypseUser && !apocalypseUser.actionTaken) {
                result += `Apocalypse! Discard a ${currentColor} Card or Draw`;
            } else {
                const waiting = apocalypse.tracker.filter(user => !user.actionTaken).map(user => user.name).join(' and ');
                result += `Apocalypse! Waiting for ${waiting}`;
            }
        } else if (fairPlay.id === currentUser.id) {
            result += ` Cards to Discard: ${fairPlay.amount}`;
        } else if (fairPlay.id) {
            const discardingUser = users.find(user => user.id === fairPlay.id);
            if (discardingUser) {
                result += ` Waiting for ${discardingUser.name} to Discard`;
            }
        }

        return result;
    };

    const getPlayAreaStyle = () => {
        const apocalypseUser = apocalypse.tracker.find(({ id }) => id === currentUser.id);
        if (gameOver ||
            slapState ||
            fairPlay.id ||
            (apocalypse.active && (!apocalypseUser || apocalypseUser.actionTaken)) ||
            (!apocalypse.active && turn !== currentUser.id) ||
            (responseWindow.playerMakingSelection && responseWindow.playerMakingSelection !== currentUser.id)) {
            return { pointerEvents: 'none' as const };
        }
        return {};
    };

    const getSlapButtonStyle = () => {
        const hasSlapped = slapTracker.some(({ id }) => id === currentUser.id);
        const canSlap = slapState && !hasSlapped && !gameOver;
        return {
            disabled: !canSlap,
            style: { pointerEvents: canSlap ? 'all' as const : 'none' as const }
        };
    };

    const getHeatedButtonStyle = () => {
        const canSayHeated = sayHeatedTracker?.active && !gameOver;
        return {
            disabled: !canSayHeated,
            style: { pointerEvents: canSayHeated ? 'all' as const : 'none' as const }
        };
    };

    // =============================================================================
    // EVENT HANDLERS
    // =============================================================================

    const handleDrawCardForFacedownDeck = async () => {
        if (gameOver || !(apocalypse.active || turn === currentUser.id || slapState)) return;

        const userCantDraw = users.find(user => user.id === currentUser.id)?.hand.some(card => isCardPlayable(card));
        if (userCantDraw && !apocalypse.active) return;

        if (apocalypse.active) {
            const apocalypseUser = apocalypse.tracker.find(({ id }) => id === currentUser.id);
            if (!apocalypseUser || apocalypseUser.canDiscard) return;

            const drawnCard = drawCardPile.pop();
            if (drawnCard) {
                users[povPlayerIndex].hand.push(drawnCard);
                const updatedTracker = apocalypse.tracker.map(user =>
                    user.id === currentUser.id ? { ...user, actionTaken: true } : user
                );

                const allDone = updatedTracker.every(user => user.actionTaken);
                if (allDone) {
                    setApocalypse({ active: false, color: '', tracker: [] });
                    setTurn(getNextPlayerId());
                } else {
                    setApocalypse(prev => ({ ...prev, tracker: updatedTracker }));
                }

                updateGameState({ users: users, apocalypse: apocalypse });
            }
        } else {
            // Regular draw
            const drawnCard = drawCardPile.pop();
            if (drawnCard) {
                users[povPlayerIndex].hand.push(drawnCard);
                updateGameState({ users: users, turn: currentUser.id });
            }
        }
    };

    const handleSlap = () => {
        socket.emit('slap', currentUser.id);
        setSlapTracker(prev => [...prev, { id: currentUser.id }]);

        if (slapTracker.length === users.length - 1) {
            const slapLoser = users.find(user => !slapTracker.some(slap => slap.id === user.id));
            if (slapLoser) {
                makePlayerDrawCards(slapLoser.id, 2);
                setSlapTracker([]);
                setSlapState(false);
                updateGameState({
                    turn: getNextPlayerId(),
                    slapState: false,
                    slapTracker: [],
                    users: users
                });
                socket.emit('slapTrackerUpdate', []);
                socket.emit('slapStateUpdate', false);
            }
        }
    };

    const handleSayHeated = () => {
        // Find the player with one card (if any)
        const playerWithOneCard = users.find(user => user.hand.length === 1);

        if (playerWithOneCard) {
            if (currentUser.id === playerWithOneCard.id) {
                const newSayHeatedTracker = sayHeatedTracker.tracker.map(user =>
                    user.id === currentUser.id
                        ? { ...user, saidHeated: true }
                        : user
                );
                // Always deactivate when HEATED is clicked
                setSayHeatedTracker({
                    active: false,
                    tracker: newSayHeatedTracker
                });
                updateGameState({ sayHeatedTracker: { active: false, tracker: newSayHeatedTracker }, users: users });
            } else {
                // If someone else says "heated", make the player with one card draw 2
                makePlayerDrawCards(playerWithOneCard.id, 2);
                setSayHeatedTracker(prev => {
                    const newState = {
                        ...prev,
                        active: false,
                        tracker: prev.tracker.map(user =>
                            user.id === playerWithOneCard.id
                                ? { ...user, saidHeated: true }
                                : user
                        )
                    };
                    updateGameState({ sayHeatedTracker: newState, users: users });
                    return newState;
                });
            }
        }
        else {
            setSayHeatedTracker({
                active: false,
                tracker: []
            });
            updateGameState({ sayHeatedTracker: { active: false, tracker: [] } });
        }
    };

    // =============================================================================
    // DRAG AND DROP HANDLERS
    // =============================================================================

    function getCardIndexFromHandById(hand: Card[], id: string) {
        return hand.findIndex(card => card.id === id);
    }

    const handleDragStart = (event: any) => {
        const activeIndex = getCardIndexFromHandById(povPlayerHand, event.active.id);
        if (povPlayerHand && povPlayerHand[activeIndex]) {
            setDragAndDropOverlayImg(povPlayerHand[activeIndex].image);
        }
    };

    const handleDragEnd = (event: { active: any; over: any; }) => {
        const { active, over } = event;
        if (!active || !over) return;

        const cardThatIsBeingDragged = povPlayerHand.find(card => card.id === active.id);
        const indexOfCardThatIsBeingDragged = getCardIndexFromHandById(povPlayerHand, active.id);

        if (!cardThatIsBeingDragged) return;

        // Reordering within hand
        if (active.id !== over.id && over.id.includes("#")) {
            const oldIndex = getCardIndexFromHandById(povPlayerHand, active.id);
            const newIndex = getCardIndexFromHandById(povPlayerHand, over.id);
            const reorderedHand = arrayMove(povPlayerHand, oldIndex, newIndex);
            setPovPlayerHand(reorderedHand);
            users[povPlayerIndex].hand = reorderedHand;
            return;
        }

        // Playing card to center area
        if (over.id === 'play-area-drop-zone') {
            handleCardClick(cardThatIsBeingDragged, indexOfCardThatIsBeingDragged, null);
            return;
        }

        // Offering card to another player
        if (cardEffectIsBeingResolved || hasOfferedCardBeenAcceptedOrRejectedOnThisTurn) return;

        const targetUser = users.find(user => user.id === over.id);
        if (targetUser && targetUser.id === turn) {
            socket.emit("offerCard", users[povPlayerIndex].id, cardThatIsBeingDragged.id, offeredCards);
        }
    };

    const acceptCardOffer = (playerIdOfOneWhoseCardIsBeingAccepted: string) => {
        const offeredCard = offeredCards.find(offer => offer.playerId === playerIdOfOneWhoseCardIsBeingAccepted);
        if (!offeredCard) return;

        const givingUser = users.find(user => user.hand.some(card => card.id === offeredCard.cardId));
        if (!givingUser) return;

        const cardToGive = givingUser.hand.find(card => card.id === offeredCard.cardId);
        if (!cardToGive) return;

        // Transfer card
        givingUser.hand = givingUser.hand.filter(card => card.id !== offeredCard.cardId);
        users[povPlayerIndex].hand.push(cardToGive);
        setPovPlayerHand(users[povPlayerIndex].hand);

        socket.emit("offeredCardHasBeenAccepted");
        updateGameState({ users: users });
    };

    // =============================================================================
    // GAME SETUP AND SOCKET EFFECTS
    // =============================================================================

    useEffect(() => {
        if (povPlayerIndex !== -1 && users[povPlayerIndex]) {
            setPovPlayerHand(users[povPlayerIndex].hand);
        }
    }, [povPlayerIndex, users]);

    useEffect(() => {
        const userIndex = users.findIndex(user => user.id === currentUser.id);
        if (userIndex !== -1) {
            setPovPlayerIndex(userIndex);
        }
    }, [currentUser, users]);

    // Reshuffle deck when low
    useEffect(() => {
        if (drawCardPile.length !== 0 && playedCardsPile.length !== 0 && drawCardPile.length < 46) {
            const cardsToGoOnBottomOfDrawPile = [...playedCardsPile];
            const lastPlayedCard = cardsToGoOnBottomOfDrawPile.pop()!;
            updateGameState({
                drawCardPile: shuffleArray(cardsToGoOnBottomOfDrawPile).concat(drawCardPile),
                playedCardsPile: [lastPlayedCard]
            });
        }
    }, [drawCardPile.length, drawCardPile, playedCardsPile, updateGameState]);

    // Game initialization
    useEffect(() => {
        if (!gameStarted || users.length < 2) return;

        let shuffledDeck = shuffleArray([...heatedBaseGameDeck]);
        let firstCard = shuffledDeck.pop()!;

        // Ensure first card is a number
        while (firstCard.type !== "Number") {
            shuffledDeck = [firstCard, ...shuffledDeck];
            firstCard = shuffledDeck.pop()!;
        }

        const playedCardsPile = [firstCard];

        // Deal 7 cards to each player
        for (let i = 0; i < users.length; i++) {
            users[i].hand = shuffledDeck.splice(0, 7);
            // users[i].hand.push(testCards["No, Thanks"]); // test card
            // users[i].hand.push(testCards["Getting Heated"]); // test card
        }

        const drawCardPile = shuffledDeck;
        const userIndex = users.findIndex(user => user.id === currentUser.id);
        if (userIndex !== -1) {
            setPovPlayerIndex(userIndex);
            setPovPlayerHand(users[userIndex].hand);
        }

        socket.emit('initGameState', {
            gameOver: false,
            turn: users[0].id,
            users: users,
            currentColor: firstCard.color,
            playedCardsPile: playedCardsPile,
            drawCardPile: drawCardPile,
            orderOfPlay: "standard",
            slapState: false,
            slapTracker: [],
            fairPlay: { id: '', amount: 0, color: '' },
            apocalypse: { active: false, color: '', tracker: [] },
            sayHeatedTracker: { active: false, tracker: [] },
            cardTargetUsers: [],
            timerLength: 5
        });
    }, [gameStarted, users.length]);

    // Socket setup
    useEffect(() => {
        const connectionOptions = {
            "forceNew": true,
            "reconnectionAttempts": "Infinity",
            "timeout": 10000,
            "transports": ["websocket"]
        };

        // @ts-expect-error
        socket = io.connect(ENDPOINT, connectionOptions);

        socket.emit('join', { room: room }, (error: string) => {
            if (error) setRoomFull(true);
        });

        socket.on('initGameState', (gameState: GameState) => {
            setGameOver(false);
            setTurn(gameState.turn);
            setCurrentColor(gameState.currentColor);
            setPlayedCardsPile(gameState.playedCardsPile);
            setDrawCardPile(gameState.drawCardPile);
            setUsers(gameState.users);
            setOrderOfPlay(gameState.orderOfPlay);
            setSlapState(gameState.slapState);
            setSlapTracker(gameState.slapTracker);
            setFairPlay(gameState.fairPlay);
            setApocalypse(gameState.apocalypse);
            setSayHeatedTracker(gameState.sayHeatedTracker);
            setCardTargetUsers(gameState.cardTargetUsers);
        });

        socket.on('updateGameState', (gameState: GameState) => {
            gameState.users && setUsers(gameState.users);
            gameState.winner && (setGameOver(true));
            gameState.turn && setTurn(gameState.turn);
            gameState.currentColor && setCurrentColor(gameState.currentColor);
            gameState.playedCardsPile && setPlayedCardsPile(gameState.playedCardsPile);
            gameState.drawCardPile && setDrawCardPile(gameState.drawCardPile);
            gameState.orderOfPlay && setOrderOfPlay(gameState.orderOfPlay);
            gameState.slapState !== undefined && setSlapState(gameState.slapState);
            gameState.slapTracker && setSlapTracker(gameState.slapTracker);
            gameState.fairPlay && setFairPlay(gameState.fairPlay);
            gameState.apocalypse && setApocalypse(gameState.apocalypse);
            gameState.sayHeatedTracker && setSayHeatedTracker(gameState.sayHeatedTracker);
            gameState.cardTargetUsers && setCardTargetUsers(gameState.cardTargetUsers);
            gameState.responseWindow && setResponseWindow(gameState.responseWindow);

            if (gameState.playedCardsPile?.length === 0) {
                setCardEffectIsBeingResolved(false);
            }
        });

        socket.on('slapTrackerUpdate', (updatedSlapTracker: { id: string }[]) => {
            setSlapTracker(updatedSlapTracker);
        });

        socket.on('slapStateUpdate', (updatedSlapState: boolean) => {
            setSlapState(updatedSlapState);
        });

        socket.on("roomData", ({ users, gameStarted, creator }: { users: User[]; gameStarted: boolean; creator: string | null }) => {
            setUsers(users);
            setRoomCreator(creator);
        });

        socket.on('currentUserData', ({ user }: { user: User }) => {
            setCurrentUser(user);
        });

        socket.on("cardHasBeenOffered", (newOfferedCards: { playerId: string, cardId: string }[]) => {
            setOfferedCards(newOfferedCards);
        });

        socket.on("offeredCardHasBeenAccepted", () => {
            setOfferedCards([]);
            setHasOfferedCardBeenAcceptedOrRejectedOnThisTurn(true);
        });

        socket.on("cardEffectIsBeingResolved", () => {
            setCardEffectIsBeingResolved(true);
        });

        socket.on("gameStarted", (data: {timerLength?: number} = {}) => {
            setGameStarted(true);
        });

        // Response window events
        socket.on('responseWindowStarted', (data: { timeRemaining: number, triggerCard: Card, targetedPlayers: string[] }) => {
            setResponseWindow(prev => ({
                ...prev,
                active: true,
                triggerCard: data.triggerCard,
                timeRemaining: data.timeRemaining,
                targetedPlayers: data.targetedPlayers
            }));
        });

        socket.on('responseWindowUpdate', (data: { timeRemaining: number }) => {
            setResponseWindow(prev => ({
                ...prev,
                timeRemaining: data.timeRemaining
            }));
        });

        socket.on('responseWindowEnded', (data: { affectedPlayers: string[], triggerCard: Card }) => {
            setResponseWindow(prev => ({
                ...prev,
                active: false
            }));

            // Server handles all the card effects and turn advancement
            // Client just needs to update the response window state
        });

        return () => {
            socket.disconnect();
            socket.removeAllListeners();
        };
    }, [room]);

    // Background music
    useEffect(() => {
        if (isMusicMuted) {
            stopBgMusic();
        } else {
            playBgMusic();
        }
    }, [isMusicMuted, stopBgMusic, playBgMusic]);

    // Update states when turn changes
    useEffect(() => {
        setOfferedCards([]);
        setHasOfferedCardBeenAcceptedOrRejectedOnThisTurn(false);
        setCardEffectIsBeingResolved(false);
    }, [turn]);

    const handleChangeName = (newName: string) => {
        socket.emit('changeName', newName);
    };

    const handleStartGame = (timerLength: number) => {
        if (roomCreator === currentUser.id && users.length >= 2) {
            socket.emit('startGame', {timerLength});
        }
    };

    // Add this useEffect near your other useEffect hooks
    useEffect(() => {
        if (users.length === 0 || turn === '') return;

        if (!apocalypse.active) {
            updateGameState({
                users: users,
                playedCardsPile: playedCardsPile,
                apocalypse: apocalypse,
            });
        } else {
            updateGameState({
                users: users,
                playedCardsPile: playedCardsPile,
                apocalypse: apocalypse
            });
        }
    }, [apocalypse.active]);

    useEffect(() => {
        updateGameState({
            fairPlay: fairPlay,
        });
    }, [fairPlay.amount]);

    // Add this effect to monitor players' hands and update sayHeatedTracker state
    useEffect(() => {
        // Only handle activation when tracker is inactive
        if (!sayHeatedTracker.active) {
            const playersWithOneCard = users.filter(user => user.hand.length === 1);
            if (playersWithOneCard.length > 0) {
                // Only activate if there are players with one card who haven't said heated
                const playersWhoNeedToSayHeated = playersWithOneCard.filter(user =>
                    !sayHeatedTracker.tracker.some(tracked =>
                        tracked.id === user.id && tracked.saidHeated
                    )
                );

                if (playersWhoNeedToSayHeated.length > 0) {
                    const newTracker = playersWhoNeedToSayHeated.map(user => ({
                        id: user.id,
                        name: user.name,
                        saidHeated: false
                    }));

                    setSayHeatedTracker({
                        active: true,
                        tracker: newTracker
                    });
                    updateGameState({
                        sayHeatedTracker: {
                            active: true,
                            tracker: newTracker
                        }
                    });
                }
            }
            else {
                setSayHeatedTracker({
                    active: false,
                    tracker: []
                });
            }
        }
    }, [users]);

    // =============================================================================
    // RENDER
    // =============================================================================

    if (!gameStarted) {
        return (
            <Lobby
                users={users}
                currentUser={currentUser}
                roomCode={room}
                isCreator={roomCreator === currentUser.id}
                creatorId={roomCreator}
                onStartGame={handleStartGame}
                onChangeName={handleChangeName}
            />
        );
    }

    return (
        <div className={`HeatedGame background-color-${currentColor}`}>
            {!roomFull ? (
                <>
                    {modalType && (
                        <ModernCardActionModal
                            users={users}
                            currentUser={currentUser}
                            show={true}
                            onClose={modalCloseHandlers[modalType] || (() => setModalType(null))}
                            type={modalType}
                        />
                    )}

                    <motion.div
                        className='topInfo'
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <motion.img
                            src={heatedLogo}
                            alt="Heated Game Logo"
                            whileHover={{ scale: 1.05 }}
                            transition={{ duration: 0.2 }}
                        />
                        <TopInformationBar
                            gameCode={room}
                            idOfPlayerWhoseTurnItIs={turn}
                            currentColor={currentColor}
                            users={users}
                        />

                        {/* Winner Banner */}
                        {gameOver && winner && (
                            <motion.div
                                className="winner-banner"
                                initial={{ opacity: 0, y: -50 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <div className="winner-content">
                                    <span className="winner-text">🎉 GAME OVER 🎉</span>
                                    <span className="winner-name">{winner} WINS!</span>
                                </div>
                            </motion.div>
                        )}

                        {/* Response Timer */}
                        {responseWindow.active && !gameOver && (
                            <motion.div
                                className="response-timer"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="timer-content">
                                    <span className="timer-text">Card Played against:</span>
                                    <span className="timer-text-targeted-players">{responseWindow.targetedPlayers.map(player => users.find(u => u.id === player)?.name).join(", ")}</span>
                                    <span className="timer-text">Respond in</span>
                                    <span className="timer-count">{responseWindow.timeRemaining}s</span>
                                </div>
                            </motion.div>
                        )}

                        {/* Selection In Progress Indicator */}
                        {responseWindow.playerMakingSelection && !gameOver && (
                            <motion.div
                                className="selection-indicator"
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="selection-content">
                                    <span className="selection-text">
                                        {responseWindow.playerMakingSelection === currentUser.id
                                            ? "Make your selection..."
                                            : `${users.find(u => u.id === responseWindow.playerMakingSelection)?.name} is making a selection...`
                                        }
                                    </span>
                                </div>
                            </motion.div>
                        )}
                        <div className="sound-controls">
                            <motion.button
                                className='sound-button'
                                onClick={() => setSoundMuted(!isSoundMuted)}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                title={isSoundMuted ? "Enable sound effects" : "Disable sound effects"}
                            >
                                <AnimatePresence mode="wait">
                                    {isSoundMuted ? (
                                        <motion.div key="volume-off" initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} exit={{ scale: 0, rotate: 180 }} transition={{ duration: 0.2 }}>
                                            <VolumeX size={20} />
                                        </motion.div>
                                    ) : (
                                        <motion.div key="volume-on" initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} exit={{ scale: 0, rotate: 180 }} transition={{ duration: 0.2 }}>
                                            <Volume2 size={20} />
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.button>

                            <motion.button
                                className='sound-button'
                                onClick={() => setMusicMuted(!isMusicMuted)}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                title={isMusicMuted ? "Enable background music" : "Disable background music"}
                            >
                                <AnimatePresence mode="wait">
                                    {isMusicMuted ? (
                                        <motion.div key="music-off" initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} exit={{ scale: 0, rotate: 180 }} transition={{ duration: 0.2 }}>
                                            <VolumeX size={20} />
                                        </motion.div>
                                    ) : (
                                        <motion.div key="music-on" initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} exit={{ scale: 0, rotate: 180 }} transition={{ duration: 0.2 }}>
                                            <Music4 size={20} />
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.button>
                        </div>
                    </motion.div>

                    {users.length === 1 && <h1 className='topInfoText'>Waiting for more players to join the game.</h1>}

                    {users.length >= 2 && (
                        <DndContext
                                sensors={sensors}
                                collisionDetection={pointerWithin}
                                onDragEnd={handleDragEnd}
                                onDragStart={handleDragStart}
                            >
                                <div className='play-area-and-players'>
                                    <div className='other-player-hands-container'>
                                        <div className='play-area-top-row'>
                                            <OtherPlayerHand
                                                userCount={users.length}
                                                currentPositionOfDiv='top-right'
                                                currentUserId={currentUser.id}
                                                idForWhoseTurnItIs={turn}
                                                users={users}
                                                offeredCards={offeredCards}
                                                acceptCardOffer={acceptCardOffer}
                                            />
                                        </div>

                                        <div className='play-area-left-column'>
                                            <OtherPlayerHand
                                                userCount={users.length}
                                                currentPositionOfDiv='left-top'
                                                currentUserId={currentUser.id}
                                                idForWhoseTurnItIs={turn}
                                                users={users}
                                                offeredCards={offeredCards}
                                                acceptCardOffer={acceptCardOffer}
                                            />
                                        </div>

                                        <div className='play-area-right-column'>
                                            <OtherPlayerHand
                                                userCount={users.length}
                                                currentPositionOfDiv='right-bottom'
                                                currentUserId={currentUser.id}
                                                idForWhoseTurnItIs={turn}
                                                users={users}
                                                offeredCards={offeredCards}
                                                acceptCardOffer={acceptCardOffer}
                                            />
                                            <OtherPlayerHand
                                                userCount={users.length}
                                                currentPositionOfDiv='right-center'
                                                currentUserId={currentUser.id}
                                                idForWhoseTurnItIs={turn}
                                                users={users}
                                                offeredCards={offeredCards}
                                                acceptCardOffer={acceptCardOffer}
                                            />
                                            <OtherPlayerHand
                                                userCount={users.length}
                                                currentPositionOfDiv='right-top'
                                                currentUserId={currentUser.id}
                                                idForWhoseTurnItIs={turn}
                                                users={users}
                                                offeredCards={offeredCards}
                                                acceptCardOffer={acceptCardOffer}
                                            />
                                        </div>
                                    </div>

                                    <div className='center-row-play-area'>
                                        <DropZone id="play-area-drop-zone">
                                            <div className={`play-area play-area-${currentColor} ${responseWindow.playerMakingSelection && responseWindow.playerMakingSelection !== currentUser.id ? 'selection-blocked' : ''}`} style={getPlayAreaStyle()}>
                                                {playedCardsPile.length > 0 && (
                                                    <img
                                                        className='top-discarded-heated-card'
                                                        src={playedCardsPile[playedCardsPile.length - 1].image}
                                                        alt={`Top discarded card: ${playedCardsPile[playedCardsPile.length - 1].name}`}
                                                        ref={topOfDiscardPileRef}
                                                    />
                                                )}

                                                <div className='draw-card-pile-container' style={{ "cursor": !(apocalypse.active || turn === currentUser.id || slapState) ? "initial" : "pointer" }}>
                                                    <div className='draw-card-pile-images' onClick={handleDrawCardForFacedownDeck}>
                                                        <img src={cardBackImg} className='draw-card-pile-img' alt="Draw pile card back" />
                                                        <img src={cardBackImg} className='draw-card-pile-img img-2' alt="Draw pile card back" />
                                                        <img src={cardBackImg} className='draw-card-pile-img img-3' alt="Draw pile card back" />
                                                        <img src={cardBackImg} className='draw-card-pile-img drawn-card' alt="Drawn card animation" ref={drawnCardRef} />
                                                    </div>
                                                    <div className='draw-card-pile-text'>
                                                        <p>Click deck to draw</p>
                                                    </div>
                                                </div>

                                                <button
                                                    className={`slap-button ${slapState ? 'active' : ''}`}
                                                    {...getSlapButtonStyle()}
                                                    onClick={handleSlap}
                                                >
                                                    Slap!
                                                </button>

                                                <button
                                                    className={`heated-button `}
                                                    {...getHeatedButtonStyle()}
                                                    onClick={handleSayHeated}
                                                >
                                                    HEATED!
                                                </button>
                                            </div>
                                        </DropZone>
                                    </div>

                                    <CurrentPlayerHand
                                        renderPlayerText={renderPlayerText}
                                        handOfCurrentPlayer={povPlayerHand}
                                        handleCardClick={handleCardClick}
                                        name={users[povPlayerIndex].name}
                                        handleCardReorderingInPlayerHand={() => {}}
                                        offeredCards={offeredCards}
                                    />

                                    <DragOverlay dropAnimation={null}>
                                        <img
                                            className='player-heated-card-overlay'
                                            style={{ width: "7rem" }}
                                            src={dragAndDropOverlayImg}
                                            alt="Dragged card"
                                        />
                                    </DragOverlay>
                                </div>
                            </DndContext>
                    )}
                </>
            ) : (
                <div><h1>Room full</h1></div>
            )}

            {users.length <= 1 && (
                <>
                    <motion.a href='/' whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }} style={{ textDecoration: 'none' }}>
                        <button className="heated-button" style={{ margin: '0 auto' }}>QUIT</button>
                    </motion.a>
                </>
            )}
        </div>
    );
};

export default HeatedGame;
